import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updateAdminPlacementTestTemplate } from '../../../shared/api/placement-tests.services';
import { ADULT_PLACEMENT_TEST_SLUG } from '../../../shared/config/testSlugs';
import toast from '../../../shared/lib/toast';

const validationSchema = Yup.object({
  title: Yup.object({
    ua: Yup.string().required('Назва UA необхідна'),
    en: Yup.string().required('Назва EN необхідна'),
  }),
  slug: Yup.object({
    ua: Yup.string(),
    en: Yup.string(),
  }),
  intro: Yup.object({
    questionCountMin: Yup.number().required('Мінімальна кількість питань необхідна'),
    questionCountMax: Yup.number().required('Максимальна кількість питань необхідна'),
    estimatedTimeMin: Yup.number().required('Мінімальний час необхідний'),
    estimatedTimeMax: Yup.number().required('Максимальний час необхідний'),
    benefits: Yup.array().of(Yup.string()),
  }),
  settings: Yup.object({
    questionCount: Yup.number().required('Кількість питань необхідна'),
    randomizeQuestions: Yup.boolean(),
    randomizeOptions: Yup.boolean(),
  }),
  cefrScale: Yup.array().of(
    Yup.object({
      level: Yup.string().required('Рівень необхідний'),
      title: Yup.object({ ua: Yup.string().required('Назва рівня необхідна') }),
      minScore: Yup.number().required('Мінімальний бал необхідний'),
      maxScore: Yup.number().required('Максимальний бал необхідний'),
    })
  ),
  questionPool: Yup.array().of(
    Yup.object({
      id: Yup.string(),
      type: Yup.string(),
      prompt: Yup.object({
        ua: Yup.string().required('Текст питання необхідний'),
        en: Yup.string().required('Текст питання необхідний'),
      }),
      helperText: Yup.mixed().nullable(),
      groups: Yup.array().of(
        Yup.object({
          id: Yup.string(),
          label: Yup.mixed(),
          inputKind: Yup.string(),
          weight: Yup.number(),
          acceptedAnswers: Yup.array().of(Yup.string()),
          options: Yup.array().of(
            Yup.object({
              id: Yup.string(),
              label: Yup.string(),
              text: Yup.object({
                ua: Yup.string(),
                en: Yup.string(),
              }),
              isCorrect: Yup.boolean(),
            })
          ),
        })
      ),
    })
  ),
});

const normalizeToLocalized = (val) => {
  if (typeof val === 'string') {
    return { ua: val, en: val };
  }
  if (typeof val === 'object' && val !== null && (val.ua !== undefined || val.en !== undefined)) {
    return val;
  }
  return { ua: '', en: '' };
};

const normalizeString = (val) => {
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null) {
    return val.ua || val.en || '';
  }
  return '';
};

// Сильні сторони й зони росту в базі перекладені (ua != en). Раніше вони
// схлопувались у рядок, тож будь-яке збереження тесту стирало англійську.
const normalizeLocalizedArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.map((item) => normalizeToLocalized(item));
};

const normalizeArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.map(item =>
    typeof item === 'string' ? item : (typeof item === 'object' && item !== null ? (item.ua || item.en || '') : '')
  );
};

const buildInitialValues = (template) => {
  if (!template) {
    return {
      title: { ua: '', en: '' },
      slug: { ua: '', en: '' },
      isActive: true,
      intro: {
        questionCountMin: 20,
        questionCountMax: 40,
        estimatedTimeMin: 15,
        estimatedTimeMax: 20,
        benefits: [],
      },
      settings: {
        questionCount: 20,
        randomizeQuestions: true,
        randomizeOptions: false,
      },
      cefrScale: [],
      questionPool: [],
    };
  }

  return {
    title: normalizeToLocalized(template.title),
    slug: normalizeToLocalized(template.slug),
    isActive: template.isActive !== false,
    intro: {
      questionCountMin: template.intro?.questionCountMin || 20,
      questionCountMax: template.intro?.questionCountMax || 40,
      estimatedTimeMin: template.intro?.estimatedTimeMin || 15,
      estimatedTimeMax: template.intro?.estimatedTimeMax || 20,
      benefits: normalizeArray(template.intro?.benefits),
    },
    settings: {
      questionCount: template.settings?.questionCount || 20,
      randomizeQuestions: template.settings?.randomizeQuestions ?? true,
      randomizeOptions: template.settings?.randomizeOptions ?? false,
    },
    cefrScale: (Array.isArray(template.cefrScale) ? template.cefrScale : []).map(level => ({
      level: normalizeString(level.level),
      title: normalizeToLocalized(level.title),
      minScore: level.minScore || 0,
      maxScore: level.maxScore || 0,
      summary: normalizeToLocalized(level.summary),
      strengths: normalizeLocalizedArray(level.strengths),
      growthAreas: normalizeLocalizedArray(level.growthAreas),
      recommendedCourse: {
        code: level.recommendedCourse?.code || '',
        name: normalizeToLocalized(level.recommendedCourse?.name),
        url: level.recommendedCourse?.url || '',
      },
    })),
    questionPool: (Array.isArray(template.questionPool) ? template.questionPool : []).map(q => {
      const type = normalizeString(q.type) || 'single_choice_group';
      const isTextQuestion = type === 'text_input_group';

      return {
        id: normalizeString(q.id),
        type,
        prompt: normalizeToLocalized(q.prompt),
        helperText: q.helperText ? normalizeToLocalized(q.helperText) : null,
        groups: Array.isArray(q.groups) ? q.groups.map(g => {
          const base = {
            id: normalizeString(g.id),
            label: normalizeToLocalized(g.label),
          };

          if (isTextQuestion) {
            const group = {
              ...base,
              inputKind: normalizeString(g.inputKind) || 'short',
              weight: typeof g.weight === 'number' ? g.weight : 1,
              acceptedAnswers: Array.isArray(g.acceptedAnswers)
                ? g.acceptedAnswers.map(a => normalizeString(a)).filter(Boolean)
                : [],
            };

            // Рамка слів (choices) — те, з чого студент обирає у випадайці.
            // Раніше форма її не читала, тому будь-яке збереження стирало
            // підказки з завдань про прийменники та дієслова.
            if (Array.isArray(g.choices) && g.choices.length > 0) {
              group.choices = g.choices.map((c) => normalizeString(c)).filter(Boolean);
            }

            return group;
          }

          return {
            ...base,
            weight: typeof g.weight === 'number' ? g.weight : 1,
            options: Array.isArray(g.options) ? g.options.map(o => ({
              id: normalizeString(o.id),
              label: normalizeString(o.label),
              text: normalizeToLocalized(o.text),
              isCorrect: o.isCorrect || false,
            })) : [],
          };
        }) : [],
      };
    }),
  };
};

// Форма обслуживает и взрослый Placement Test, и детский тест: разметка
// и правила у них одинаковые, отличаются только эндпоинт сохранения и слаг,
// который подставляется, если в шаблоне его почему-то нет.
const usePlacementTestForm = (
  initialData,
  templateId,
  {
    fallbackSlug = ADULT_PLACEMENT_TEST_SLUG,
    saveTemplate = updateAdminPlacementTestTemplate,
  } = {}
) => {
  const formik = useFormik({
    initialValues: buildInitialValues(initialData),
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Ensure slug is always a string, handle both object and string formats
        let slug = values.slug;
        if (typeof slug === 'object' && slug !== null) {
          slug = slug.en || slug.ua || fallbackSlug;
        }
        if (!slug || slug === '[object Object]') {
          slug = fallbackSlug;
        }

        const cleanPairs = (list) =>
          (list || []).filter((item) => (item?.ua || '').trim() || (item?.en || '').trim());

        const payload = {
          ...values,
          cefrScale: (values.cefrScale || []).map((level) => ({
            ...level,
            strengths: cleanPairs(level.strengths),
            growthAreas: cleanPairs(level.growthAreas),
          })),
          // Назву шлемо об'єктом: раніше вона схлопувалась в один рядок і
          // українська версія затиралась англійською при кожному збереженні.
          title: typeof values.title === 'object' ? values.title : normalizeToLocalized(values.title),
          slug: slug,
        };

        const response = await saveTemplate(templateId, payload);

        if (response?.error) {
          toast.error(response.message || 'Помилка при збереженні');
          return;
        }

        toast.success('Шаблон успішно оновлено!');
      } catch (error) {
        console.error('Error updating template:', error);
        toast.error('Помилка при збереженні шаблону');
      }
    },
  });

  return formik;
};

export { usePlacementTestForm };
